import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function authHeaders(includeJson = false) {
  const token = localStorage.getItem('token');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (includeJson) headers['Content-Type'] = 'application/json';
  return headers;
}

/**
 * Manages the multi-note thread for a single truck.
 *
 * @param {Object} opts
 * @param {'inbound'|'outbound'} opts.truckType
 * @param {number|string|null|undefined} opts.truckId
 * @param {boolean} [opts.enabled] default true
 */
export default function useTruckNotes({ truckType, truckId, enabled = true }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const canFetch =
    enabled &&
    (truckType === 'inbound' || truckType === 'outbound') &&
    truckId !== null &&
    truckId !== undefined &&
    truckId !== '';

  const fetchNotes = useCallback(async () => {
    if (!canFetch) {
      setNotes([]);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ truck_type: truckType, truck_id: String(truckId) });
      const res = await fetch(`${API_URL}/truck-notes?${params.toString()}`, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Failed to load notes (${res.status})`);
      const data = await res.json();
      setNotes(Array.isArray(data.notes) ? data.notes : []);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('useTruckNotes fetch error:', err);
      setError(err.message || 'Failed to load notes');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [canFetch, truckType, truckId]);

  useEffect(() => {
    fetchNotes();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchNotes]);

  const addNote = useCallback(
    async (content) => {
      if (!canFetch) return null;
      const res = await fetch(`${API_URL}/truck-notes`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ truck_type: truckType, truck_id: truckId, content }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `Failed to add note (${res.status})`);
      }
      const data = await res.json();
      setNotes((prev) => [...prev, data.note]);
      return data.note;
    },
    [canFetch, truckType, truckId]
  );

  const editNote = useCallback(async (noteId, content) => {
    const res = await fetch(`${API_URL}/truck-notes/${noteId}`, {
      method: 'PUT',
      headers: authHeaders(true),
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || `Failed to update note (${res.status})`);
    }
    const data = await res.json();
    setNotes((prev) => prev.map((n) => (n.id === noteId ? data.note : n)));
    return data.note;
  }, []);

  const removeNote = useCallback(async (noteId) => {
    const res = await fetch(`${API_URL}/truck-notes/${noteId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || `Failed to delete note (${res.status})`);
    }
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
  }, []);

  return { notes, loading, error, refresh: fetchNotes, addNote, editNote, removeNote };
}
